import type * as api from "@screencloud/billing-client-api";
import { Constants } from "src/billinglatest/constants";
import type { PixiAddress } from "../../../hooks/usePixiWizardState";

/**
 * Map the wizard's `PixiAddress` onto the billing SDK's customer-address request
 * (`updateCustomerAddress`). `line1`/`zip`/`country` are the required fields; the
 * rest are best-effort optionals.
 *
 * Chargebee stores both a free-text `state` and an abbreviated `stateCode`, and
 * exactly one must be sent per country or it stores both and mis-resolves — the
 * billing page's address form keys this off `Constants.States` (a dropdown for
 * those countries, a text field otherwise) and clears the unused field. US/CA
 * are the countries in that list today, and they reject the address without
 * `stateCode` ("State code is required for US and CA"). The wizard collects the
 * value in its single `state` field, so we route it the same way.
 */
export const pixiAddressToBillingAddress = (
  address: PixiAddress,
): api.Customers.Me.Address.Request => {
  const requiresStateCode = Object.keys(Constants.States).includes(
    address.country,
  );
  return {
    firstName: address.firstName || undefined,
    lastName: address.secondName || undefined,
    email: address.email || undefined,
    company: address.company || undefined,
    phone: address.phone || undefined,
    line1: address.street,
    line2: address.building || undefined,
    city: address.city || undefined,
    state: requiresStateCode ? undefined : address.state || undefined,
    stateCode: requiresStateCode ? address.state || undefined : undefined,
    zip: address.postalCode,
    country: address.country,
  };
};
